/**
 * Client memory of Collect&Go Chrome extension install / activity / sign-in.
 * Used to keep settings + order UI compact without waiting on (or flipping after) probes.
 */

const DOWNLOADED_AT = 'cogo-ext-downloaded-at'
const LAST_SEEN_AT = 'cogo-ext-last-seen-at'
const LAST_VERSION = 'cogo-ext-last-version'
const LAST_SIGNED_IN_AT = 'cogo-ext-last-signed-in-at'

/** Treat extension as "recently active" within this window. */
const RECENT_EXT_MS = 7 * 24 * 60 * 60 * 1000
/** Treat Collect&Go session as "recently signed in" within this window. */
const RECENT_SIGNED_IN_MS = 7 * 24 * 60 * 60 * 1000

function readNum(key: string): number | null {
  try {
    const v = localStorage.getItem(key)
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function writeNum(key: string, n: number) {
  try {
    localStorage.setItem(key, String(n))
  } catch {
    /* ignore */
  }
}

function readStr(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStr(key: string, v: string) {
  try {
    localStorage.setItem(key, v)
  } catch {
    /* ignore */
  }
}

export type CogoExtensionPrefs = {
  downloadedAt: number | null
  lastSeenAt: number | null
  lastVersion: string | null
  lastSignedInAt: number | null
  everActivated: boolean
  recentlyActive: boolean
  everSignedIn: boolean
  recentlySignedIn: boolean
}

export function getCogoExtensionPrefs(): CogoExtensionPrefs {
  const downloadedAt = readNum(DOWNLOADED_AT)
  const lastSeenAt = readNum(LAST_SEEN_AT)
  const lastVersion = readStr(LAST_VERSION)
  const lastSignedInAt = readNum(LAST_SIGNED_IN_AT)
  const now = Date.now()
  const everActivated = lastSeenAt != null
  const recentlyActive = lastSeenAt != null && now - lastSeenAt < RECENT_EXT_MS
  const everSignedIn = lastSignedInAt != null
  const recentlySignedIn =
    lastSignedInAt != null && now - lastSignedInAt < RECENT_SIGNED_IN_MS
  return {
    downloadedAt,
    lastSeenAt,
    lastVersion,
    lastSignedInAt,
    everActivated,
    recentlyActive,
    everSignedIn,
    recentlySignedIn,
  }
}

/** Call when the user starts a zip download. */
export function markCogoExtensionDownloaded() {
  writeNum(DOWNLOADED_AT, Date.now())
}

/** Call when a live probe confirms the extension is present. */
export function markCogoExtensionSeen(version: string | null) {
  writeNum(LAST_SEEN_AT, Date.now())
  if (version) writeStr(LAST_VERSION, version)
}

/** Call when checkAuth (or equivalent) confirms a signed-in Collect&Go session. */
export function markCogoSignedIn() {
  writeNum(LAST_SIGNED_IN_AT, Date.now())
}

/**
 * Layout mode for settings — derived only from localStorage so first paint
 * matches later paint (no expand/collapse when the probe returns).
 */
export type CogoExtensionUiMode = 'setup' | 'installing' | 'ready'

export function cogoExtensionUiMode(
  prefs: CogoExtensionPrefs = getCogoExtensionPrefs()
): CogoExtensionUiMode {
  if (prefs.everActivated || prefs.recentlyActive) return 'ready'
  if (prefs.downloadedAt != null) return 'installing'
  return 'setup'
}

/**
 * True when this browser can host the Collect&Go Chrome extension
 * (Chrome/Chromium/Brave on desktop — not mobile, Safari, Firefox, Edge).
 */
export function canRunCogoChromeExtension(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/Android|iPhone|iPod|iPad/i.test(ua)) return false
  // iPadOS desktop-class UA
  if (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) return false
  if (/Edg\/|OPR\/|Firefox\//i.test(ua)) return false
  return /(Chrome|Chromium)\//.test(ua)
}
